import React, { useEffect, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import Match3 from "@/game/Match3";
import { LOGO_URL } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Barcode } from "@/components/Barcode";
import { Gamepad2, Trophy, Ticket, Crown, Copy, Lock, PartyPopper, Gift, Bell, Star, History, ArrowUpRight, ArrowDownRight, CalendarClock } from "lucide-react";

const TABS = [
  { k: "point", label: "Point Member", icon: Star },
  { k: "game", label: "Mini Game", icon: Gamepad2 },
  { k: "board", label: "Leaderboard", icon: Trophy },
  { k: "vouchers", label: "Voucher Saya", icon: Ticket },
  { k: "notif", label: "Info", icon: Bell },
];

function bellTwice() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const beep = (t) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination); o.type = "sine"; o.frequency.value = 880;
      g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.35, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.28); o.start(t); o.stop(t + 0.3);
    };
    beep(ctx.currentTime); beep(ctx.currentTime + 0.36);
  } catch (e) { /* audio unsupported */ }
}

export default function MemberDashboard() {
  const [tab, setTab] = useState("point");
  const [status, setStatus] = useState(null);
  const [board, setBoard] = useState({ rows: [], me: null });
  const [vouchers, setVouchers] = useState([]);
  const [points, setPoints] = useState(0);
  const [barcode, setBarcode] = useState("");
  const [history, setHistory] = useState([]);
  const [pointVouchers, setPointVouchers] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const notifInited = useRef(false);
  const saveTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const [s, lb, v, wks, nt, lm, pv, hist] = await Promise.all([
        api.get("/game/status"), api.get("/game/leaderboard"), api.get("/vouchers/mine"), api.get("/game/weeks"), api.get("/notifications"), api.get("/loyalty/me"), api.get("/point-vouchers"), api.get("/loyalty/history"),
      ]);
      setStatus(s.data); setBoard(lb.data); setVouchers(v.data); setWeeks(wks.data);
      setPoints(lm.data?.points || 0); setPointVouchers(pv.data || []);
      setBarcode(lm.data?.barcode || lm.data?.phone || "");
      setHistory(hist.data || []);
      const list = nt.data || [];
      setNotifs(list);
      let seen = [];
      try { seen = JSON.parse(localStorage.getItem("jjl_notif_seen") || "[]"); } catch (e) {}
      const newOnes = list.filter((n) => !seen.includes(n.id));
      if (!notifInited.current) {
        notifInited.current = true;
        localStorage.setItem("jjl_notif_seen", JSON.stringify(list.map((n) => n.id)));
      } else if (newOnes.length > 0) {
        bellTwice();
        toast(`🔔 ${newOnes[0].title}`, { description: newOnes[0].message });
        setUnread((u) => u + newOnes.length);
        localStorage.setItem("jjl_notif_seen", JSON.stringify(list.map((n) => n.id)));
      }
    } catch (e) { /* offline */ }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const onScore = (score) => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try { await api.post("/game/score", { score }); load(); } catch {}
    }, 1200);
  };

  const locked = status?.locked;

  return (
    <div className="min-h-screen bg-hotpot-grid pb-10">
      <TopBar />
      <div className="mx-auto max-w-6xl px-4 pt-6 sm:px-6">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="mb-6 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight text-neutral-900 sm:text-3xl">Loyalty Arena 🔥</h1>
            <p className="text-sm text-muted-foreground">
              Kumpulkan skor tertinggi, menangkan e-voucher hotpot mingguan!
            </p>
          </div>
          <div className="rounded-full border border-neutral-200 bg-neutral-100 px-4 py-2 text-xs font-semibold text-gold">
            {status ? `${status.day} · ${status.week}` : "..."}
          </div>
        </motion.div>

        <div className="mb-6 grid grid-cols-5 gap-2 rounded-2xl bg-neutral-100 p-1.5">
          {TABS.map((t) => (
            <button key={t.k} data-testid={`member-tab-${t.k}`} onClick={() => setTab(t.k)}
              className={`flex touch-btn items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold transition-all ${
                tab === t.k ? "bg-crimson text-white shadow-lg" : "text-muted-foreground hover:text-white"}`}>
              <t.icon size={16} /> <span className="hidden sm:inline">{t.label}</span>
              {t.k === "notif" && unread > 0 && <span data-testid="notif-unread-dot" className="ml-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-gold px-1 text-[10px] font-bold text-black">{unread}</span>}
            </button>
          ))}
        </div>

        {tab === "game" && (
          <div className="space-y-4">
            <ScheduleNote status={status} weekActive={!!weeks.find((w) => w.is_current)?.active} />
            <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
              <Card className="card-surface rounded-2xl p-3 sm:p-5">
                {locked ? ((status?.sunday && weeks.find((w) => w.is_current)?.active) ? <SundayLock board={board} week={weeks.find((w) => w.is_current)} /> : <InactiveLock />) : (
                  <Match3 onScore={onScore} initialScore={board.me?.score || 0} onExplosion={() => toast("🔥 RESTAURANT EXPLOSION!", { className: "bg-crimson" })} />
                )}
              </Card>
              <div className="space-y-4">
                <MiniLeaderboard board={board} />
                <PrizeCard week={weeks.find((w) => w.is_current)} />
              </div>
            </div>
          </div>
        )}

        {tab === "point" && <PointMember points={points} barcode={barcode} vouchers={pointVouchers} history={history} onRedeem={load} />}
        {tab === "board" && <FullLeaderboard board={board} />}
        {tab === "vouchers" && <Vouchers vouchers={vouchers} />}
        {tab === "notif" && <MemberNotifications notifs={notifs} onOpen={() => setUnread(0)} />}
      </div>
    </div>
  );
}

function MemberNotifications({ notifs, onOpen }) {
  useEffect(() => { onOpen?.(); }, [onOpen]);
  return (
    <div className="space-y-3" data-testid="member-notifications">
      {notifs.length === 0 && (
        <Card className="card-surface rounded-2xl p-10 text-center">
          <Bell className="mx-auto mb-3 text-muted-foreground" size={40} />
          <p className="text-muted-foreground">Belum ada notifikasi.</p>
        </Card>
      )}
      {notifs.map((n) => (
        <Card key={n.id} data-testid={`member-notif-${n.id}`} className="card-surface rounded-2xl border-l-4 border-l-crimson p-4">
          <div className="flex items-center gap-2 font-display font-bold text-neutral-900"><Bell size={16} className="text-crimson" /> {n.title}</div>
          <p className="mt-1 text-sm text-muted-foreground">{n.message}</p>
          <div className="mt-2 text-[11px] text-muted-foreground">{new Date(n.created_at).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
        </Card>
      ))}
    </div>
  );
}

function tierColor(rank) {
  return rank === 1 ? "text-gold" : rank === 2 ? "text-slate-300" : rank === 3 ? "text-amber-600" : "text-muted-foreground";
}

function MiniLeaderboard({ board }) {
  return (
    <Card className="card-surface rounded-2xl p-4">
      <div className="mb-3 flex items-center gap-2 font-display font-bold text-neutral-900"><Trophy size={16} className="text-gold" /> Top Minggu Ini</div>
      <div className="space-y-2">
        {board.rows.slice(0, 5).map((r) => (
          <div key={r.user_id + r.rank} className="flex items-center justify-between rounded-lg bg-neutral-100 px-3 py-2">
            <div className="flex items-center gap-2">
              <span className={`font-mono-num text-sm font-bold ${tierColor(r.rank)}`}>#{r.rank}</span>
              <span className="text-sm text-neutral-900">{r.name}</span>
            </div>
            <span className="font-mono-num text-sm font-bold text-gold">{r.score.toLocaleString("id-ID")}</span>
          </div>
        ))}
        {board.rows.length === 0 && <p className="text-xs text-muted-foreground">Belum ada skor. Jadilah yang pertama!</p>}
      </div>
    </Card>
  );
}

function FullLeaderboard({ board }) {
  return (
    <Card className="card-surface rounded-2xl p-5" data-testid="leaderboard-full">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2 font-display text-xl font-bold text-neutral-900"><Crown className="text-gold" /> Leaderboard Mingguan</div>
        <span className="text-xs text-muted-foreground">{board.week}</span>
      </div>
      <div className="space-y-2">
        {board.rows.map((r) => (
          <motion.div key={r.user_id + r.rank} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
            className={`flex items-center justify-between rounded-xl border px-4 py-3 ${
              r.rank <= 3 ? "border-gold/40 bg-gold/5" : "border-neutral-200 bg-neutral-100"}`}>
            <div className="flex items-center gap-3">
              <span className={`font-mono-num text-lg font-extrabold ${tierColor(r.rank)}`}>{r.rank <= 3 ? "👑" : ""}#{r.rank}</span>
              <span className="font-semibold text-neutral-900">{r.name}</span>
            </div>
            <span className="font-mono-num text-lg font-bold text-gold">{r.score.toLocaleString("id-ID")}</span>
          </motion.div>
        ))}
        {board.rows.length === 0 && <p className="text-center text-sm text-muted-foreground py-8">Belum ada data leaderboard.</p>}
      </div>
    </Card>
  );
}

function PointMember({ points, barcode, vouchers, history = [], onRedeem }) {
  const [busy, setBusy] = useState(null);
  const redeem = async (v) => {
    if (points < v.point_cost) { toast.error("Poin belum cukup"); return; }
    if (!window.confirm(`Tukar ${v.point_cost.toLocaleString("id-ID")} poin dengan "${v.name}"? Voucher berlaku 7 hari.`)) return;
    setBusy(v.id);
    try { const { data } = await api.post(`/point-vouchers/${v.id}/redeem`); toast.success(`Berhasil ditukar! Kode: ${data.code}. Cek di "Voucher Saya".`); onRedeem?.(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(null); }
  };
  return (
    <div className="space-y-5" data-testid="point-member">
      <Card className="card-surface gold-glow flex items-center justify-between rounded-2xl p-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Poin Anda</div>
          <div data-testid="point-balance" className="flex items-center gap-2 font-display text-4xl font-extrabold text-gold">
            <Star size={30} className="fill-yellow-400 text-yellow-400" /> {points.toLocaleString("id-ID")}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">Setiap belanja Rp1.000 = 1 poin. Poin tidak pernah hangus.</div>
        </div>
      </Card>

      <Card className="card-surface rounded-2xl p-5" data-testid="member-barcode-card">
        <div className="mb-3 flex items-center gap-2 font-display font-bold text-neutral-900"><Ticket size={18} className="text-crimson" /> Kartu Member</div>
        {barcode ? (
          <div className="flex flex-col items-center rounded-xl bg-white p-4">
            <Barcode value={barcode} height={70} width={2} className="w-full max-w-sm" />
            <div data-testid="member-barcode-value" className="mt-2 font-mono-num text-base font-bold tracking-widest text-neutral-900">{barcode}</div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Nomor HP belum tersedia untuk kartu member.</p>
        )}
        <p className="mt-3 text-center text-xs text-muted-foreground">Tunjukkan barcode ini ke kasir (berisi No HP Anda) untuk mengumpulkan poin di setiap transaksi.</p>
      </Card>

      <div>
        <div className="mb-3 flex items-center gap-2 font-display text-lg font-bold text-neutral-900"><Ticket size={18} className="text-yellow-500" /> Tukar Poin dengan E-Voucher</div>
        {vouchers.length === 0 ? (
          <Card className="card-surface rounded-2xl p-10 text-center"><p className="text-muted-foreground">Belum ada E-Voucher poin tersedia. Nantikan penawaran berikutnya!</p></Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {vouchers.map((v) => {
              const enough = points >= v.point_cost;
              return (
                <Card key={v.id} data-testid={`point-voucher-${v.id}`} className="card-surface rounded-2xl border border-yellow-400/60 p-4 shadow-[0_0_22px_rgba(250,204,21,0.22)]">
                  <div className="mb-1 inline-flex rounded-full bg-yellow-400 px-2 py-0.5 text-[10px] font-bold uppercase text-black">⭐ E-Voucher Poin</div>
                  <div className="font-display text-lg font-extrabold text-neutral-900">{v.name}</div>
                  <div className="mt-0.5 text-2xl font-extrabold text-yellow-600">{v.discount_type === "nominal" ? `Rp ${Number(v.discount_value).toLocaleString("id-ID")}` : `${v.discount_value}%`}</div>
                  <div className="mt-1 text-xs text-muted-foreground">Berlaku 7 hari setelah ditukar</div>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="font-mono-num text-sm font-bold text-yellow-600">{v.point_cost.toLocaleString("id-ID")} poin</span>
                    <Button data-testid={`redeem-point-${v.id}`} disabled={!enough || busy === v.id} onClick={() => redeem(v)}
                      className="h-9 bg-yellow-400 font-bold text-black hover:bg-yellow-300 disabled:opacity-50">
                      {busy === v.id ? "..." : enough ? "Tukar" : "Poin Kurang"}
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <div className="mb-3 flex items-center gap-2 font-display text-lg font-bold text-neutral-900"><History size={18} className="text-crimson" /> Riwayat Poin</div>
        {history.length === 0 ? (
          <Card className="card-surface rounded-2xl p-8 text-center"><p className="text-sm text-muted-foreground">Belum ada riwayat poin. Poin masuk otomatis setiap kali belanja dengan member terpasang.</p></Card>
        ) : (
          <Card className="card-surface rounded-2xl p-2" data-testid="point-history">
            <div className="divide-y divide-neutral-200">
              {history.map((h, k) => {
                const earn = h.type === "earn";
                return (
                  <div key={h.id || k} data-testid={`point-history-${k}`} className="flex items-center justify-between gap-3 px-3 py-3">
                    <div className="flex items-center gap-3">
                      <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${earn ? "bg-emerald-500/15 text-emerald-500" : "bg-crimson/10 text-crimson"}`}>
                        {earn ? <ArrowUpRight size={18} /> : <ArrowDownRight size={18} />}
                      </span>
                      <div>
                        <div className="text-sm font-semibold text-neutral-900">{h.label || (earn ? "Poin Masuk" : "Poin Keluar")}</div>
                        <div className="text-[11px] text-muted-foreground">{h.created_at ? new Date(h.created_at).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</div>
                      </div>
                    </div>
                    <span className={`font-mono-num text-sm font-bold ${earn ? "text-emerald-500" : "text-crimson"}`}>{earn ? "+" : ""}{Number(h.points).toLocaleString("id-ID")} poin</span>
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

function Vouchers({ vouchers }) {
  const copy = (code) => { navigator.clipboard?.writeText(code); toast.success("Kode voucher disalin"); };
  return (
    <div className="grid gap-4 sm:grid-cols-2" data-testid="my-vouchers">
      {vouchers.length === 0 && (
        <Card className="card-surface col-span-full rounded-2xl p-10 text-center">
          <Ticket className="mx-auto mb-3 text-muted-foreground" size={40} />
          <p className="text-muted-foreground">Belum ada voucher. Menangkan permainan mingguan atau tukar poin untuk mendapatkannya!</p>
        </Card>
      )}
      {vouchers.map((v) => {
        const point = v.source === "point";
        const gift = v.source === "gift";
        const expDate = v.expires_at ? new Date(v.expires_at) : null;
        const expired = expDate ? Date.now() > expDate.getTime() : false;
        const daysLeft = expDate ? Math.ceil((expDate.getTime() - Date.now()) / 86400000) : null;
        const dead = v.status === "used" || expired;
        const frame = point ? "border border-yellow-400/60 shadow-[0_0_22px_rgba(250,204,21,0.28)]" : gift ? "border border-emerald-400/50 shadow-[0_0_22px_rgba(52,211,153,0.22)]" : "gold-glow";
        const badgeBg = point ? "bg-yellow-400 text-black" : gift ? "bg-emerald-500 text-white" : "bg-crimson text-white";
        const badgeLabel = point ? "⭐ Poin" : gift ? "🎁 Hadiah" : v.type === "nominal" ? "Diskon Rp" : v.type === "percent" ? "Diskon %" : "Free Item";
        const codeColor = point ? "text-yellow-600" : gift ? "text-emerald-400" : "text-gold";
        return (
        <Card key={v.id} data-testid={`voucher-card-${v.code}`}
          className={`card-surface flex flex-col gap-3 rounded-2xl p-4 ${dead ? "opacity-60" : frame}`}>
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className={`mb-1 inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${badgeBg}`}>
                {badgeLabel}
              </div>
              <div className="font-display text-xl font-extrabold text-neutral-900">
                {v.type === "nominal" ? `Rp ${v.value.toLocaleString("id-ID")}` : v.type === "percent" ? `${v.value}%` : v.free_item}
              </div>
              {v.label ? <div className="text-xs text-muted-foreground">{v.label}</div> : null}
            </div>
            <div className={`text-right text-xs font-semibold ${expired ? "text-crimson" : v.status === "active" ? "text-emerald-400" : "text-muted-foreground"}`}>
              {v.status === "used" ? "○ Sudah Diredeem" : expired ? "✕ Kedaluwarsa" : "● Aktif"}
            </div>
          </div>
          <div className="flex flex-col items-center rounded-xl bg-white p-3">
            <Barcode value={v.code} height={56} width={2} className="w-full max-w-[240px]" />
            <button onClick={() => copy(v.code)} className={`mt-1 flex items-center gap-1.5 font-mono-num text-sm tracking-widest hover:underline ${codeColor}`}>
              {v.code} <Copy size={12} />
            </button>
          </div>
          {!dead && (
            <div data-testid={`voucher-exp-${v.code}`} className="text-[11px] text-muted-foreground">
              {expDate ? `Berlaku s/d ${expDate.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" })}${daysLeft != null ? ` · ${daysLeft} hari lagi` : ""}` : "Tanpa batas waktu"}
            </div>
          )}
        </Card>
        );
      })}
    </div>
  );
}

function voucherDesc(w) {
  if (w.voucher_type === "nominal") return `Voucher Rp ${Number(w.voucher_value).toLocaleString("id-ID")}`;
  if (w.voucher_type === "percent") return `Diskon ${w.voucher_value}%`;
  return `Gratis ${w.free_item_name || "Item"}`;
}
const MEDALS = ["🥇", "🥈", "🥉"];
function PrizeCard({ week }) {
  return (
    <Card className="card-surface overflow-hidden rounded-2xl" data-testid="weekly-prize-card">
      <img src="https://images.unsplash.com/photo-1614104030967-5ca61a54247b?crop=entropy&cs=srgb&fm=jpg&q=85"
        alt="hotpot" className="h-28 w-full object-cover" />
      <div className="p-4">
        <div className="mb-2 flex items-center gap-2 font-display font-bold text-neutral-900"><Gift size={16} className="text-gold" /> Hadiah Mingguan</div>
        {!week || !week.active ? (
          <p className="text-xs text-muted-foreground" data-testid="prize-empty">Belum ada hadiah untuk minggu ini. Nantikan jadwal berikutnya!</p>
        ) : (
          <>
            <p className="mb-3 text-xs text-muted-foreground">Top {week.winners_count} pemain minggu ini masing-masing mendapat:</p>
            <div className="space-y-2" data-testid="prize-list">
              {Array.from({ length: week.winners_count }).map((_, i) => (
                <div key={i} data-testid={`prize-rank-${i + 1}`} className="flex items-center justify-between rounded-lg border border-gold/30 bg-gold/5 px-3 py-2">
                  <span className="flex items-center gap-2 text-sm font-bold text-neutral-900">{MEDALS[i] || "🏅"} Peringkat #{i + 1}</span>
                  <span className="font-mono-num text-xs font-bold text-gold">{voucherDesc(week)}</span>
                </div>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-muted-foreground">Dibagikan otomatis tiap Senin 00:00 WIB · reset ke minggu baru.</p>
          </>
        )}
      </div>
    </Card>
  );
}

function ScheduleNote({ status, weekActive }) {
  const sunday = !!status?.sunday;
  const reason = sunday
    ? "Hari ini Minggu — game ditutup untuk Pengumuman Pemenang."
    : !weekActive
    ? "Minggu ini belum dibuka oleh restoran. Nantikan jadwal berikutnya."
    : "Game dibuka! Kumpulkan skor tertinggi Anda sekarang.";
  return (
    <Card className="card-surface rounded-2xl border-l-4 border-l-gold p-4" data-testid="game-schedule-note">
      <div className="flex items-start gap-3">
        <CalendarClock size={18} className="mt-0.5 shrink-0 text-gold" />
        <div className="text-sm text-neutral-700">
          <div className="font-display font-bold text-neutral-900">Jadwal Permainan</div>
          <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
            <li><span className="font-semibold text-neutral-800">🎮 Senin–Sabtu:</span> waktu bermain &amp; kumpulkan skor tertinggi (untuk minggu yang diaktifkan restoran).</li>
            <li><span className="font-semibold text-neutral-800">🏆 Minggu:</span> game ditutup — Pengumuman Pemenang.</li>
            <li><span className="font-semibold text-neutral-800">🎁 Senin 00:00 WIB:</span> e-voucher dibagikan otomatis ke pemenang, lalu leaderboard reset ke minggu baru.</li>
          </ul>
          <div className="mt-2 rounded-lg bg-gold/10 px-2.5 py-1.5 text-xs font-semibold text-neutral-800" data-testid="game-status-note">{reason}</div>
        </div>
      </div>
    </Card>
  );
}

function InactiveLock() {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="inactive-lock">
      <img src={LOGO_URL} alt="" className="h-24 w-24 rounded-full bg-white/90 p-1" />
      <div className="mt-4 flex items-center gap-2 text-muted-foreground"><Lock size={16} /><span className="text-xs font-semibold uppercase tracking-wider">Game Nonaktif</span></div>
      <h2 className="mt-2 font-display text-2xl font-extrabold text-neutral-900">Belum Ada Permainan Minggu Ini</h2>
      <p className="max-w-sm text-sm text-muted-foreground">Nantikan jadwal game berikutnya. Pantau terus untuk kesempatan memenangkan e-voucher hotpot!</p>
    </div>
  );
}

function SundayLock({ board, week }) {
  const count = week?.winners_count || 3;
  const winners = board.rows.slice(0, count);
  return (
    <div className="flex flex-col items-center justify-center py-10 text-center" data-testid="sunday-lock">
      <motion.div animate={{ rotate: [0, -8, 8, 0] }} transition={{ repeat: Infinity, duration: 3 }}>
        <img src={LOGO_URL} alt="" className="h-24 w-24 rounded-full bg-white/90 p-1" />
      </motion.div>
      <div className="mt-4 flex items-center gap-2 text-gold"><Lock size={16} /><span className="font-semibold uppercase tracking-wider text-xs">Game Terkunci · Hari Minggu</span></div>
      <h2 className="mt-2 font-display text-2xl font-extrabold text-neutral-900 flex items-center gap-2"><PartyPopper className="text-gold" /> Pengumuman Pemenang</h2>
      <p className="max-w-md text-sm text-muted-foreground">Selamat kepada <b>Top {count}</b> pemain minggu ini! E-voucher dibagikan otomatis <b>Senin 00:00 WIB</b>.</p>
      <div className="mt-5 w-full max-w-md space-y-2" data-testid="sunday-winners">
        {winners.length === 0 && <p className="text-sm text-muted-foreground">Belum ada pemain yang mencetak skor minggu ini.</p>}
        {winners.map((r) => (
          <div key={r.rank} data-testid={`sunday-winner-${r.rank}`} className="flex items-center justify-between gap-3 rounded-xl border border-gold/40 bg-gold/5 px-4 py-3">
            <span className="font-display font-bold text-neutral-900">{MEDALS[r.rank - 1] || "🏅"} #{r.rank} {r.name}</span>
            <div className="flex flex-col items-end">
              <span className="font-mono-num text-sm font-bold text-neutral-900">{r.score.toLocaleString("id-ID")}</span>
              {week && <span className="font-mono-num text-xs font-bold text-gold">{voucherDesc(week)}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
